import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import type { Lang } from '@polyglot/engine';
import { LANGS } from '@polyglot/engine';
import { BackLink, Spinner } from '../components/ui';
import { api, ApiError, ensureSession, useMe } from '../lib/api';
import { applyCosmetics } from '../lib/cosmetics';
import { formatScore } from '../lib/format';
import { flushPractice, pendingPractice } from '../lib/practice';
import { isAndroidApp } from '../lib/platform';

interface Progress {
  streak: {
    current: number;
    best: number;
    last_day: string | null;
    grace_left: number;
    timezone: string;
  };
  rewards: { kind: string; ref: string; created_at: string }[];
  wordsFound: number;
  distinctWords: number;
  roundsPlayed: number;
}

interface Cosmetics {
  catalogue: { id: string; kind: 'theme' | 'avatar' | 'frame'; source: string }[];
  owned: string[];
  equipped: Record<string, string>;
  plusOffered: boolean;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export function Profile() {
  const { t } = useTranslation();
  const { user } = useMe();
  const [progress, setProgress] = useState<Progress | null>(null);
  const [cosmetics, setCosmetics] = useState<Cosmetics | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    await ensureSession();
    const [p, c] = await Promise.all([
      api<Progress>('GET', '/api/me/progress'),
      api<Cosmetics>('GET', '/api/me/cosmetics'),
    ]);
    setProgress(p);
    setCosmetics(c);
    applyCosmetics(c.equipped);
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  if (!progress || !cosmetics) return <Spinner label={t('common.loading')} />;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const badges = [...new Set(progress.rewards.map((r) => r.kind))];

  const equip = async (slot: 'theme' | 'avatar' | 'frame', id: string) => {
    const r = await api<{ equipped: Record<string, string> }>('PATCH', '/api/me/equipped', {
      [slot]: id,
    });
    setCosmetics({ ...cosmetics, equipped: r.equipped });
    applyCosmetics(r.equipped);
  };

  return (
    <main className="page">
      <BackLink />
      <h1>{user?.displayName ?? t('profile.title')}</h1>
      <section className="card stats-grid">
        <div>
          <span className="big-number">{progress.streak.current}</span>
          <span className="muted small">{t('profile.streak')}</span>
        </div>
        <div>
          <span className="big-number">{progress.streak.best}</span>
          <span className="muted small">{t('profile.bestStreak')}</span>
        </div>
        <div>
          <span className="big-number">{progress.roundsPlayed}</span>
          <span className="muted small">{t('profile.rounds')}</span>
        </div>
        <div>
          <span className="big-number">{progress.distinctWords}</span>
          <span className="muted small">{t('profile.distinctWords')}</span>
        </div>
      </section>
      <p className="muted small">
        {t('profile.streakRules', {
          grace: progress.streak.grace_left,
          tz: progress.streak.timezone,
        })}{' '}
        <Link href="/daily">{t('profile.playDaily')}</Link>
      </p>
      <MyGames />
      {progress.streak.timezone !== tz && (
        <button
          className="btn ghost small"
          onClick={async () => {
            try {
              await api('PUT', '/api/me/timezone', { timezone: tz });
              setMessage(t('account.saved'));
              await load();
            } catch (e) {
              setMessage(
                e instanceof ApiError && e.code === 'timezone_change_too_soon'
                  ? t('profile.tzTooSoon')
                  : t('errors.generic'),
              );
            }
          }}
        >
          {t('profile.useMyTimezone', { tz })}
        </button>
      )}
      {message && <p className="message info">{message}</p>}

      <section className="card">
        <h2>{t('profile.badges')}</h2>
        {badges.length === 0 ? (
          <p className="muted">{t('profile.noBadges')}</p>
        ) : (
          <ul className="badge-list">
            {badges.map((b) => (
              <li key={b} className="badge-item">
                {t(`badges.${b}`, { defaultValue: b })}
              </li>
            ))}
          </ul>
        )}
        <p className="muted small">{t('profile.badgesNote')}</p>
      </section>

      <section className="card">
        <h2>{t('profile.cosmetics')}</h2>
        {(['theme', 'avatar', 'frame'] as const).map((slot) => (
          <div key={slot} className="row wrap cosmetic-row">
            <strong>{t(`profile.slot.${slot}`)}</strong>
            {cosmetics.catalogue
              .filter((c) => c.kind === slot)
              .map((c) => {
                const owned = cosmetics.owned.includes(c.id);
                return (
                  <button
                    key={c.id}
                    className={`chip small${cosmetics.equipped[slot] === c.id ? ' selected' : ''}`}
                    disabled={!owned}
                    title={
                      owned
                        ? undefined
                        : t(c.source === 'plus' ? 'profile.plusOnly' : 'profile.earnIt')
                    }
                    onClick={() => void equip(slot, c.id)}
                  >
                    {t(`cosmetics.${c.id}`, { defaultValue: c.id })}
                    {!owned && (c.source === 'plus' ? ' ✦' : ' 🔒')}
                  </button>
                );
              })}
          </div>
        ))}
        <p className="muted small">{t('profile.cosmeticsNote')}</p>
      </section>

      <Reminders />

      <nav className="home-links">
        <Link href="/journal">{t('journal.title')}</Link>
        <Link href="/friends">{t('friends.title')}</Link>
        <Link href="/team">{t('team.title')}</Link>
        {cosmetics.plusOffered && !isAndroidApp() && <Link href="/plus">Plus</Link>}
        <Link href="/account">{t('account.title')}</Link>
      </nav>
    </main>
  );
}

function Reminders() {
  const { t } = useTranslation();
  const [config, setConfig] = useState<{ enabled: boolean; publicKey: string | null } | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [quietStart, setQuietStart] = useState(22);
  const [quietEnd, setQuietEnd] = useState(8);
  useEffect(() => {
    api<{ enabled: boolean; publicKey: string | null }>('GET', '/api/push/config')
      .then(setConfig)
      .catch(() => setConfig({ enabled: false, publicKey: null }));
  }, []);
  if (!config) return null;
  const supported =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

  const enable = async () => {
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') return setStatus(t('reminders.denied'));
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(config.publicKey!) as BufferSource,
      });
      await api('POST', '/api/push/subscription', sub.toJSON());
      await api('PATCH', '/api/me', {
        notificationPrefs: {
          reminders: true,
          quietStart,
          quietEnd,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });
      setStatus(t('reminders.enabled'));
    } catch {
      setStatus(t('errors.generic'));
    }
  };
  const disable = async () => {
    await api('DELETE', '/api/push/subscription').catch(() => {});
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      await (await reg?.pushManager.getSubscription())?.unsubscribe();
    } catch {
      /* ignore */
    }
    setStatus(t('reminders.disabled'));
  };

  // No push on this server (v1): no section at all, rather than a feature announced as missing.
  if (!config.enabled) return null;
  return (
    <section className="card">
      <h2>{t('reminders.title')}</h2>
      <p className="muted small">{t('reminders.explain')}</p>
      {!supported ? (
        <p className="muted">{t('reminders.unavailable')}</p>
      ) : (
        <>
          <div className="row wrap">
            <label className="field">
              <span>{t('reminders.quietStart')}</span>
              <input
                type="number"
                min={0}
                max={23}
                value={quietStart}
                onChange={(e) => setQuietStart(Number(e.target.value))}
              />
            </label>
            <label className="field">
              <span>{t('reminders.quietEnd')}</span>
              <input
                type="number"
                min={0}
                max={23}
                value={quietEnd}
                onChange={(e) => setQuietEnd(Number(e.target.value))}
              />
            </label>
          </div>
          <div className="row wrap">
            <button className="btn primary" onClick={() => void enable()}>
              {t('reminders.enable')}
            </button>
            <button className="btn ghost" onClick={() => void disable()}>
              {t('reminders.disable')}
            </button>
          </div>
        </>
      )}
      {status && <p className="message info">{status}</p>}
    </section>
  );
}

interface JournalRow {
  lang: Lang;
  word_key: string;
  display: string;
  first_seen_at: string;
  times: number;
}

export function Journal() {
  const { t, i18n } = useTranslation();
  const [lang, setLang] = useState<Lang | 'all'>('all');
  const [q, setQ] = useState('');
  const [data, setData] = useState<{
    words: JournalRow[];
    counts: { lang: Lang; n: number }[];
  } | null>(null);
  useEffect(() => {
    const params = new URLSearchParams();
    if (lang !== 'all') params.set('lang', lang);
    if (q) params.set('q', q);
    void ensureSession()
      .then(() => api<typeof data>('GET', `/api/me/journal?${params.toString()}`))
      .then(setData)
      .catch(() => setData({ words: [], counts: [] }));
  }, [lang, q]);
  return (
    <main className="page">
      <BackLink to="/me" />
      <h1>{t('journal.title')}</h1>
      <p className="muted small">{t('journal.intro')}</p>
      <div className="row wrap">
        <button
          className={`chip small${lang === 'all' ? ' selected' : ''}`}
          onClick={() => setLang('all')}
        >
          {t('journal.all')}
        </button>
        {LANGS.map((l) => (
          <button
            key={l}
            className={`chip small${lang === l ? ' selected' : ''}`}
            onClick={() => setLang(l)}
          >
            {l.toUpperCase()} {data?.counts.find((c) => c.lang === l)?.n ?? 0}
          </button>
        ))}
      </div>
      <label className="field">
        <span className="sr-only">{t('journal.search')}</span>
        <input
          type="text"
          placeholder={t('journal.search')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </label>
      {!data ? (
        <Spinner label={t('common.loading')} />
      ) : data.words.length === 0 ? (
        <p className="muted">{t('journal.empty')}</p>
      ) : (
        <table className="data">
          <tbody>
            {data.words.map((w) => (
              <tr key={`${w.lang}-${w.word_key}`}>
                <td>
                  <span className={`lang-tag lang-${w.lang}`}>{w.lang.toUpperCase()}</span>
                </td>
                <td>{w.display}</td>
                <td className="num muted">×{w.times}</td>
                <td className="num muted small">
                  {new Date(w.first_seen_at).toLocaleDateString(i18n.language)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="muted small">{t('about.noDefinitions')}</p>
    </main>
  );
}

interface HistoryEntry {
  source: 'online' | 'practice';
  id: string;
  kind: string;
  mode: string;
  languages: string[] | null;
  words: number;
  final_cents: number | null;
  seat_status: string;
  bot_level: string | null;
  played_at: string;
}

/** Personal history: verified online games and local practice, clearly labelled. */
function MyGames() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<{
    counts: { online: number; practice: number };
    recent: HistoryEntry[];
  } | null>(null);
  const [pending, setPending] = useState(0);
  useEffect(() => {
    // Send any practice round finished offline first, so the list is complete.
    void flushPractice()
      .then(() => api<NonNullable<typeof data>>('GET', '/api/me/history'))
      .then(setData)
      .catch(() => setData({ counts: { online: 0, practice: 0 }, recent: [] }))
      .finally(() => setPending(pendingPractice()));
  }, []);
  if (!data) return null;
  return (
    <section className="card my-games" aria-labelledby="my-games-title">
      <h2 id="my-games-title">{t('profile.history')}</h2>
      <p>{t('profile.gamesSummary', data.counts)}</p>
      {pending > 0 && <p className="muted small">{t('profile.pendingSync', { n: pending })}</p>}
      {data.recent.length === 0 ? (
        <p className="muted">
          {t('profile.noGames')} <Link href="/">{t('home.quickPlay')}</Link>
        </p>
      ) : (
        <div className="table-scroll">
          <table className="data">
            <tbody>
              {data.recent.map((h) => (
                <tr key={`${h.source}-${h.id}`}>
                  <td>{new Date(h.played_at).toLocaleDateString(i18n.language)}</td>
                  <td>
                    {h.source === 'online' ? (
                      <Link href={`/match/${h.id}`}>{t(`modes.${h.mode}.name`)}</Link>
                    ) : (
                      t(`modes.${h.mode}.name`)
                    )}
                    {h.bot_level && ` · ⚙ ${t(`bots.${h.bot_level}`)}`}
                    <br />
                    <span className={`tag ${h.source}`}>
                      {h.source === 'online'
                        ? t('profile.sourceOnline')
                        : t('profile.sourcePractice')}
                    </span>{' '}
                    <span className="muted small">
                      {(h.languages ?? []).map((l) => l.toUpperCase()).join('+')}
                    </span>
                  </td>
                  <td className="num">
                    {h.final_cents !== null
                      ? formatScore(h.final_cents, i18n.language)
                      : h.seat_status === 'finished'
                        ? t('profile.waitingOpponent')
                        : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="muted small">{t('profile.gamesNote')}</p>
    </section>
  );
}
